import { api } from './api';
import type { ApiResponse, SubscriptionInfo, PlanType, BillingCycle } from '../types';
import { PLAN_INFO } from '../config/constants';

function transformSubscriptionResponse(data: any): SubscriptionInfo {
  return {
    plan: data.plan,
    billing_cycle: data.subscription?.billing_cycle || null,
    status: data.subscription?.status || 'inactive',
    current_period_start: data.subscription?.current_period_start || '',
    current_period_end: data.subscription?.current_period_end || '',
    cancel_at: data.subscription?.cancel_at_period_end ? data.subscription.current_period_end : null,
    stripe_subscription_id: data.subscription?.id || null,
    limits: {
      domains: data.plan_details.domains,
      storage_mb: data.plan_details.storage_mb,
      monthly_messages: data.plan_details.messages_per_month,
      chat_history_days: data.plan_details.chat_history_days ?? PLAN_INFO[data.plan as PlanType]?.chat_history_days,
      rate_limit_per_min: data.plan_details.rate_limit_per_min || PLAN_INFO[data.plan as PlanType]?.rate_limit_per_min || 10,
    },
    usage: {
      domains_used: data.usage.domains.used,
      storage_used_mb: data.usage.storage.used / (1024 * 1024),
      messages_this_month: data.usage.messages.used,
      messages_limit_type: data.usage.messages.limit_type ?? null,
      leads_this_month: data.usage.leads_this_month ?? 0,
    },
  };
}

export interface CheckoutResponse {
  checkout_url: string;
  session_id: string;
}

export interface ChangeSubscriptionResponse {
  message: string;
  plan: PlanType;
  billing_cycle: BillingCycle | null;
}

export interface CancelSubscriptionResponse {
  message: string;
  cancel_at: string;
}

export interface BillingPortalResponse {
  portal_url: string;
}

export const subscriptionService = {
  // サブスクリプション情報取得
  getSubscription: async (): Promise<ApiResponse<SubscriptionInfo>> => {
    const response = await api.get('/users/me/subscription');
    const raw = response.data;
    if (raw.success && raw.data) {
      return { success: true, data: transformSubscriptionResponse(raw.data), error: null };
    }
    return raw;
  },

  // チェックアウトセッション作成
  createCheckout: async (
    plan: PlanType,
    billingCycle: BillingCycle
  ): Promise<ApiResponse<CheckoutResponse>> => {
    const response = await api.post('/users/me/subscription/checkout', {
      plan,
      billing_cycle: billingCycle,
    });
    return response.data;
  },

  // プラン変更
  changeSubscription: async (plan: PlanType): Promise<ApiResponse<ChangeSubscriptionResponse>> => {
    const response = await api.post('/users/me/subscription/change', { plan });
    return response.data;
  },

  // サブスクリプション解約
  cancelSubscription: async (reason_category?: string, reason_detail?: string): Promise<ApiResponse<CancelSubscriptionResponse>> => {
    const response = await api.post('/users/me/subscription/cancel', { reason_category, reason_detail });
    return response.data;
  },

  // Billing Portal URL取得
  getBillingPortalUrl: async (): Promise<ApiResponse<BillingPortalResponse>> => {
    const response = await api.get('/users/me/subscription/portal');
    return response.data;
  },

};
